import Text from "hardcover-ui/components/Text";
import EditableAudioProgress from "./EditableAudioProgress/EditableAudioProgress";
import EditablePageProgress from "./EditablePageProgress/EditablePageProgress";
import EditionSelector from "components/EditionSelector/EditionSelector";
import { secondsFormat } from "lib/bookUtils";
import { audioReadingFormat, textReadingFormat } from "types/ReadingFormatType";
import EditEditionButton from "./EditEditionButton/EditEditionButton";
import Icon from "hardcover-ui/components/Icon";
import { BookSerializersBookByline, EditionSerializersEdition, UserBookSerializersUserBookDate } from "types/serializers";

export default function EditableCurrentProgress({
  onChange,
  book,
  edition,
  isOwner,
  userBookRead,
}: {
  onChange?: (progress: number) => Promise<any>;
  book: BookSerializersBookByline;
  edition: EditionSerializersEdition | null;
  isOwner: boolean;
  userBookRead: UserBookSerializersUserBookDate;
}) {
  // Need Edition to set progress, prompt them to choose an edition
  if (!edition) {
    if (isOwner) {
      return (
        <EditionSelector
          book={book}
          edition={edition}
          size="md"
          userBookRead={userBookRead}
        >
          <span className="lg:hidden">Set Edition</span>
          <span className="hidden lg:inline">
            Set edition to track progress
          </span>
        </EditionSelector>
      );
    }
    return false;
  }

  const hasDuration =
    edition.readingFormatId === audioReadingFormat.id
      ? edition?.audioSeconds > 0
      : edition?.pages > 0;

  // Edition is set, but it doesn't have a duration (pages or audioSeconds)
  if (!hasDuration) {
    if (isOwner) {
      return (
        <div className="mt-6 lg:mt-0 flex flex-row space-x-2">
          <EditionSelector
            book={book}
            edition={edition}
            size="md"
            userBookRead={userBookRead}
          >
            <span className="hidden lg:inline">Change Edition</span>
            <span className="lg:hidden">Edition</span>
          </EditionSelector>
          <EditEditionButton book={book} edition={edition} />
        </div>
      );
    }
    return false;
  }

  if (edition.readingFormatId === 2) {
    return (
      <div>
        {isOwner ? (
          <Text variant="secondary" size="xs" as="p">
            Update Your Progress
          </Text>
        ) : (
          false
        )}
        <Text
          size="lg"
          as="div"
          className="mt-1 flex w-full flex-row items-center space-x-1"
        >
          {isOwner ? (
            <EditionSelector
              book={book}
              edition={edition}
              userBookRead={userBookRead}
            >
              <>
                <Icon icon={audioReadingFormat.icon} size="md" />
              </>
            </EditionSelector>
          ) : (
            <Icon icon={audioReadingFormat.icon} size="md" />
          )}
          <span className="hidden md:inline">Currently at</span>{" "}
          {onChange ? (
            <EditableAudioProgress
              edition={edition}
              audioSeconds={edition?.audioSeconds}
              currentSeconds={userBookRead.progressSeconds}
              onChange={onChange}
            />
          ) : (
            <span>
              {userBookRead.progressSeconds
                ? secondsFormat(userBookRead.progressSeconds, {
                    showSeconds: true,
                  })
                : "0h 0m"}
            </span>
          )}
          <span> of </span>
          <b>
            {edition?.audioSeconds
              ? secondsFormat(edition?.audioSeconds)
              : "unknown length"}
          </b>
          .
        </Text>
      </div>
    );
  }
  return (
    <div>
      {isOwner ? (
        <Text variant="secondary" size="xs" as="p">
          Update Your Progress
        </Text>
      ) : (
        false
      )}
      <Text
        size="lg"
        as="div"
        className="mt-1 flex w-full flex-col md:flex-row items-start space-x-1"
      >
        {isOwner ? (
          <EditionSelector
            book={book}
            edition={edition}
            userBookRead={userBookRead}
          >
            <>
              <Icon icon={textReadingFormat.icon} size="md" />
            </>
          </EditionSelector>
        ) : (
          <Icon icon={textReadingFormat.icon} size="md" />
        )}
        <div className="flex flex-row space-x-1">
          <span>On page </span>
          {onChange ? (
            <EditablePageProgress
              edition={edition}
              pages={edition?.pages}
              currentPages={userBookRead?.progressPages}
              onChange={onChange}
            />
          ) : (
            <b>
              {userBookRead.progressPages
                ? userBookRead.progressPages.toLocaleString()
                : "0"}
            </b>
          )}
          <span> of </span>
          <b>{edition?.pages ? edition.pages.toLocaleString() : "unknown"}</b>.
        </div>
      </Text>
    </div>
  );
}
